import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowForward as ArrowIcon } from "@mui/icons-material";
import AdminMetric from "../components/admin/AdminMetric";
import AdminPage from "../components/admin/AdminPage";
import AdminSection from "../components/admin/AdminSection";
import { ADMIN_PATHS } from "../constants/adminWorkspace";
import { useAdminStats } from "../hooks/useAdmin";
import { useOpsRuns } from "../hooks/useAdminOps";
import {
  ADMIN_OVERVIEW_SHORTCUTS,
  formatAdminCount,
  summarizeOpsRuns,
} from "../utils/adminOverview";
import {
  OPS_STATUS_CHIP,
  formatOpsDuration,
} from "../utils/opsRuns";

const formatRunStarted = (value) =>
  value ? new Date(value).toLocaleString() : "—";

const AdminDashboard = () => {
  const { stats, isLoading, error } = useAdminStats();
  const {
    runs,
    isLoading: runsLoading,
    error: runsError,
  } = useOpsRuns({ page: 0, pageSize: 8 });
  const opsSummary = summarizeOpsRuns(runs);

  if (isLoading) {
    return (
      <AdminPage title="Overview" subtitle="Platform health and shortcuts">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={22} />
          <Typography color="text.secondary">Loading overview…</Typography>
        </Stack>
      </AdminPage>
    );
  }

  return (
    <AdminPage
      title="Overview"
      subtitle="Platform health and the consoles you actually use."
      error={error || runsError}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 2,
        }}
      >
        <AdminMetric
          label="Users"
          value={formatAdminCount(stats?.totalUsers)}
          hint={`${formatAdminCount(stats?.adminUsers)} admin`}
        />
        <AdminMetric
          label="Questions"
          value={formatAdminCount(stats?.totalQuestions)}
          hint={`${formatAdminCount(stats?.activeQuestions)} active`}
        />
        <AdminMetric
          label="Game sessions"
          value={formatAdminCount(stats?.totalGameSessions)}
          hint={`${formatAdminCount(stats?.completedSessions)} completed`}
        />
        <AdminMetric
          label="Recent ops"
          value={
            runsLoading ? "…" : `${opsSummary.ok}/${opsSummary.total || 0} ok`
          }
          hint={
            opsSummary.failed
              ? `${opsSummary.failed} failed`
              : opsSummary.partial
              ? `${opsSummary.partial} partial`
              : "No failures in this page"
          }
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.4fr) 320px" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <AdminSection
          title="Recent runs"
          action={
            <Button
              component={RouterLink}
              to={ADMIN_PATHS.ops}
              size="small"
              endIcon={<ArrowIcon />}
            >
              Open Ops
            </Button>
          }
        >
          {runsLoading ? (
            <Typography color="text.secondary">Loading runs…</Typography>
          ) : runs.length === 0 ? (
            <Typography color="text.secondary">
              No sync runs recorded yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {runs.map((run) => (
                <Stack
                  key={run.id}
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                  sx={{
                    py: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:last-child": { borderBottom: 0 },
                  }}
                >
                  <Box>
                    <Typography fontWeight={700}>
                      {run.sport} · {run.job}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatRunStarted(run.startedAt)} ·{" "}
                      {formatOpsDuration(run.durationMs)}
                    </Typography>
                  </Box>
                  <Chip
                    label={run.status}
                    color={OPS_STATUS_CHIP[run.status] || "default"}
                    size="small"
                  />
                </Stack>
              ))}
            </Stack>
          )}
        </AdminSection>

        <AdminSection title="Open a console">
          <Stack spacing={1.5}>
            {ADMIN_OVERVIEW_SHORTCUTS.map((item) => (
              <Box
                key={item.id}
                component={RouterLink}
                to={item.path}
                aria-label={item.title}
                sx={{
                  display: "block",
                  p: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  color: "inherit",
                  textDecoration: "none",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Typography fontWeight={800}>{item.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.body}
                </Typography>
              </Box>
            ))}
          </Stack>
        </AdminSection>
      </Box>
    </AdminPage>
  );
};

export default AdminDashboard;
